import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FetchActivityTypeLookupRequest, DeleteActivityTypeLookupRequest } from '../../../../../../../core/projects.actions';
import { activityTypeLookupData } from '../../../../../../../core/projects.selectors';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';
import { ActivityTypeSetupPopupComponent } from '../activity-type-setup-popup/activity-type-setup-popup.component';

@Component({
    selector: 'app-activity-type-setup-screen',
    templateUrl: './activity-type-setup-screen.component.html',
    styleUrls: ['./activity-type-setup-screen.component.scss'],
    standalone: false
})
export class ActivityTypeSetupScreenComponent implements OnInit, OnDestroy {
  projectId: string | null = null;

  public dataSource: any[] = [];
  public filteredDataSource: any[] = [];
  public originalDataSource: any[] = [];
  public displayedColumns: string[] = ['name', 'actions'];
  public totalRecords = 0;
  public pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  public currentPage = 1;
  public itemsPerPage = 10;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private dialog: MatDialog,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
    }

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.subTab === 'activity-categories') {
          this.fetchRecords();
        }
      });

    this.store
      .select(activityTypeLookupData)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          if (data && data?.list?.length > 0) {
            this.originalDataSource = data?.list;
            this.filteredDataSource = [...this.originalDataSource];
            this.totalRecords = data.totalRecords || this.originalDataSource.length;

            this.paginateData(true);
          } else {
            this.originalDataSource = [];
            this.filteredDataSource = [];
            this.dataSource = [];
            this.totalRecords = 0;
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  fetchRecords(): void {
    if (this.projectId) {
      const payload = {
        projectId: this.projectId,
        type: 'activity_type',
        qp: {
          start: 0,
          total: 1000,
        },
      };
      this.store.dispatch(FetchActivityTypeLookupRequest({
        payload,
      }));
    }
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);
    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  openRenamePopup(item: any): void {
    const dialogRef = this.dialog.open(ActivityTypeSetupPopupComponent, {
      width: '532px',
      height: '400px',
      disableClose: false,
      autoFocus: false,
      data: { projectId: this.projectId, item },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.fetchRecords();
      }
    });
  }

  deleteItem(item: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete',
        `Are you sure you want to delete "${item?.name || item?.value}"?`,
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.projectId) {
        const payload = {
          projectId: this.projectId,
          type: 'activity_type',
          id: item?.id,
        };
        this.store.dispatch(
          DeleteActivityTypeLookupRequest({
            payload,
          })
        );
      }
    });
  }
}

